import ConsoleUtil from "../utils/ConsoleUtil";
import IComm, { ICommRequest } from "./IComm";
import ErrorModel from "../model/ErrorModel";
import StateMachine from "../states/StateMachine";
import { DefaultStates } from "../states/DefaultStates";
import RequestData from "./RequestData";
import { Timeline } from "../Timeline";
import Utils from "../utils/Utils";
import UIUtils from "../utils/UIUtils";
import RequestRegister from "../server/requests/RequestRegister";
import { IRequestBuilder } from "../server/requests/RequestBuilder";
import Locale from "../utils/Locale";

/**
 * Handles communication with the server.
 */
export default class Comm implements IComm {
    protected readonly maxRetries: number = 10;
    protected readonly retryOffset: number = 1500; // in milliseconds
    protected readonly maxTimeout: number = 60000; // in milliseconds
    protected readonly isTimeOutEnabled: boolean = false;

    public enabled: boolean = true;

    public headers: any[] = [];

    get maxRequestsInQueue(): number {
        return 10;
    }

    private queue = new Array<RequestData>();

    /**
     * Gets a copy of the requests queue.
     *
     * @type {RequestData[]}
     * @memberof IComm
     */
    public get requestsQueue() {
        return this.queue.slice();
    }

    /**
     * Send http request to server. Upon completion, a callback is executed, if provided.
     * @param request {Object} - Pre-built Request
     * @param callback {Function} - callback to execute on successful completion
     * @param callbackScope {Object} - the scope of the callback
     * @param errorCallback {Function} - callback to execute on error
     */
    async send(request: ICommRequest) {
        if (!this.enabled) {
            return;   
        }

        // Ensure you call request.requestData getter ONLY ONCE,
        // otherwise the cheats will stop working!!!
        const params = request.builder.requestData;

        const requestData = new RequestData(params);
        if (request.enqueueRequest) {
            this.enqueueRequestData(requestData);
        }
        
        const result = await this.sendRequest(request, params, requestData, this);
        // ConsoleUtil.log(`${request.builder.requestData.action} ${result ? "SUCCESS" : "FAIL"}`);
        return result;
    }

    private async sendRequest(request: ICommRequest, params: any, data: RequestData, comm: Comm) {
        return new Promise<boolean>(async (resolve) => {
            if (!this.enabled) {
                return false;
            }

            if (!request.errorCallback) {
                request.errorCallback = (async (args: any) => {
                    if (!this.enabled) {
                        return false;
                    }

                    // Get the last data from the ALT backend

                    if (xhttp.status == 0 || Utils.isNullOrWhiteSpace(xhttp.responseText)) {
                        if (request.retryCount < this.maxRetries) {
                            // Let's retry this one - might be internet connection, etc.
                            await Utils.wait(this.retryOffset / 1000);
                            request.retryCount++;
                            ConsoleUtil.log(`Retrying request ${request.builder.requestData.action || request.builder.path}. Repeat #${request.retryCount}`);
                            const result = await this.sendRequest(request, params, data, comm);
                            if (result) {
                                resolve(true);
                            }

                            return;
                        }

                        ConsoleUtil.log("Request failed after " + request.retryCount + " retries: " + request.builder.path);
                        // No response from the server!
                        ErrorModel.errorMessage = Locale.getString('error.0');
                    }
    
                    ErrorModel.errorCode = xhttp.status;
    
                    if (args && args.data && args.data.error) {
                        ErrorModel.errorCode = args.data.error;              
                        if(args.request) {
                            if(ErrorModel.errorCode===64){
                                ErrorModel.errorMessage = "Game in maintenance mode. Please try again later.";
                            }
                            else{
                                ErrorModel.errorMessage =
                                "'" + args.request + "' request failed!";
                            }                         
                        }

                    }
    
                    if (args?.errorCode != undefined) {
                        // Probably this is a Relax error
                        ErrorModel.errorCode = args.errorCode;
                    }
    
                    if (!Utils.isNullOrWhiteSpace(args?.errorMessage)) {
                        // Probably this is a Relax error
                        ErrorModel.errorMessage = args.errorMessage;
                    }
    
                    data.onResponse(undefined, request.retryCount, ErrorModel.errorMessage);
    
                    const tryGoToErrorState = new Timeline();
                    const triesCount = 10;
                    const triesDelay = 0.2;
                    for (let index = 0; index < triesCount; index++) {
                        tryGoToErrorState.call(() => {
                            if (tryGoToErrorState.paused) {
                                return;
                            }
    
                            if (index == triesCount - 1) {
                                ErrorModel.errorMessage +=
                                "\n\nRedirection to error state was not possible!\n";
                                ConsoleUtil.error(
                                    ErrorModel.errorMessage +
                                    " (Error code: " +
                                    ErrorModel.errorCode +
                                    ")");
    
                                UIUtils.showErrorBox(ErrorModel.errorMessage, "Error " + ErrorModel.errorCode);
                            } else {
    
                                if (StateMachine.state == DefaultStates.Error) {
                                    tryGoToErrorState.pause();
                                } else {
                                    // We do not want to spin.
                                    if (StateMachine.state == DefaultStates.Idle) {
                                        tryGoToErrorState.pause();
                                        UIUtils.showErrorBox(ErrorModel.errorMessage, "Error " + ErrorModel.errorCode);
                                    } else {
                                        StateMachine.currentState.skip();
                                    }
    
                                }
                            }
                        }, [], this, index * triesDelay);
                    }
    
                    tryGoToErrorState.play();
                    resolve(false);
                }).bind(this)
            }
    
            const xhttp = new XMLHttpRequest();
            if (this.isTimeOutEnabled) {
                xhttp.timeout = this.maxTimeout;
                xhttp.ontimeout = function(e) {
                    if (!comm.enabled) {
                        return;
                    }
    
                    request.errorCallback(e);
                }
            }
    
            xhttp.onreadystatechange = function () {
                if (!comm.enabled) {
                    return;
                }

                if (this.readyState == 4 && this.status == 200) {
                    let response;
                    if (this.responseText.indexOf("html") != -1) {
                        response = this.responseText;
                    } else {
                        try {
                            response = JSON.parse(this.responseText);
                        } catch (e) {
                            request.errorCallback(e);
                            return;
                        }
                    }

                    if (comm.checkForGlobalErrors(response) == false) {
                        ConsoleUtil.log("[BaseComm] Response: ", response);
                        data.onResponse(response, request.retryCount, null);
                        request.callback(response);
                        resolve(true);
                    } else {
                        // todo - handle api error globally
                        request.errorCallback(response);
                    }
                } else {
                    // todo - handle http error
                    if (
                        (this.status == 500 || this.status == 400) &&
                        this.readyState == 4
                    ) {
                        const response = JSON.parse(this.responseText);
                        let error_type = "API_ERROR";
                        const error_id =
                            this.status == 400 ? response.code : response.requestId;
                        if (error_id == 104) {
                            error_type = "CRITICAL_ERROR";
                        }
    
                        request.errorCallback(response);
                        // TODO Display Error
                    } else if (this.status == 404 && this.readyState == 4) {
                        request.errorCallback();
                    }
                }
            };
    
            xhttp.onerror = (e) => {
                if (!comm.enabled) {
                    return;
                }

                request.errorCallback(e);
                // TODO start Error
            };
    
            if (request.builder.method == "GET") {
                const paramString = Object.keys(params)
                    .map((key) => key + "=" + encodeURIComponent(params[key]))
                    .join("&");
    
                xhttp.open(request.builder.method, request.builder.path + "?" + paramString, true);
                this.headers.forEach((el: any) => {
                    xhttp.setRequestHeader(el.name, el.value);
                });
                xhttp.send(null);
            } else if (request.builder.method == "POST") {
                xhttp.open(request.builder.method, request.builder.path, true);
                xhttp.withCredentials = request.withCredentials;
                xhttp.setRequestHeader(
                    "Content-Type",
                    "application/json; charset=UTF-8"
                );
                this.headers.forEach((el: any) => {
                    xhttp.setRequestHeader(el.name, el.value);
                });
                xhttp.send(JSON.stringify(params));
            }
        });
    }

    /**
     * Check for global errors.
     * Intended to check for errors which are global in context and not meant to be handled by the
     * controllers which initiated the communication.
     * @private
     * @param response {Object} communication response
     * @returns {boolean}
     */
    checkForGlobalErrors(response: any) {
        if (response.data && response.data.error) {
            return true;
        }

        if (response.errorCode || response.errorMessage) {
            // Probably this is a Relax FEIM error
            return true;
        }

        return false;
    }

    protected enqueueRequestData(
        data: RequestData
    ) {
        this.queue.push(data);
        if (this.queue.length > this.maxRequestsInQueue) {
            // Auto clear the queue
            this.queue.splice(0, this.queue.length - this.maxRequestsInQueue);
        }
    }
}
